import { observable, action } from 'mobx'
import { AlertType } from 'react-alert'
import { ModalComponentKeyTypes, ModalDialogDetails, Alert } from './types'
import { RootStore } from 'store/RootStore'
import { ROUTING_STORE } from 'store/RoutingStore'
import { DialogPopupProps } from 'components/shared'

export const GENERAL_STORE = 'GENERAL_STORE'

export const defaultModalDialogDetails: ModalDialogDetails = {
  show: false,
  title: '',
  description: '',
  onSubmit: () => null,
  componentKey: null
}

export class GeneralStore {
  rootStore: RootStore;

  constructor (rootStore: RootStore) {
    this.rootStore = rootStore
  }

  // ALERT
  @observable alert: Alert = { type: 'info', message: '', counter: 0 };

  handleApiError = (error: Error) => {
    let err = null
    if (this.alert.counter >= 1) return // Prevent multiple popups

    try {
      if (error.message && error.message.trim().startsWith('{')) {
        err = JSON.parse(error.message)

        if (err?.errors) this.showError(err?.errors || 'Failed to fetch.') // For Shopify integration
      } else {
        err = { message: error.message }
      }
    } catch (e) {
      console.error('Error parsing JSON: ', e)
      err = { message: 'An unknown error occurred.' }
    }

    if (err.status === 401) {
      this.setLoadingOff()
      this.rootStore[ROUTING_STORE].replace('/login')
      this.showError(err.message || 'Something went wrong')
    } else if (err.status === 403) {
      this.showError(err.message || '403 Forbidden.')
    } else if (err.status === 404) {
      this.showError(err.message || '404 Not Found.')
    } else if (err.status === 400) {
      this.showError(err.message || '400 Bad Request.')
    } else {
      this.showError(err.message || 'Failed to fetch.')
    }
  }

  @action.bound showAlert (type: AlertType, message: string): void {
    this.alert = {
      type,
      message,
      counter: this.alert.counter + 1
    }
  }

  showError = (message: string): void => {
    this.setLoadingOff()
    this.showAlert('error', message)
  }

  showInfo = (message: string): void => {
    this.showAlert('info', message)
  }

  showSuccess = (message: string): void => {
    this.showAlert('success', message)
  }

  // LOADING
  @observable loading: boolean = false;

  @action.bound setLoadingOn (): void {
    this.loading = true
  }

  @action.bound setLoadingOff (): void {
    this.loading = false
  }

  push = (route: string): void => {
    this.setLoadingOff()
    this.rootStore[ROUTING_STORE].push(route)
  }

  /** When true, sidebar shows icons + labels and reserves full width (toggled by arrow only). */
  @observable isSideMenuExpanded: boolean = false;

  @action.bound expandSideMenu () {
    this.isSideMenuExpanded = true
  }

  @action.bound collapseSideMenu () {
    this.isSideMenuExpanded = false
  }

  @action.bound switchSideMenuExpandedState () {
    this.isSideMenuExpanded = !this.isSideMenuExpanded
  }

  // MODAL DIALOG
  @observable modalDialogDetails: ModalDialogDetails = defaultModalDialogDetails;

  @action.bound setDetailsAndShowModalDialog (modal: ModalDialogDetails) {
    this.modalDialogDetails = { ...modal, show: true }
  }

  @action.bound hideModalDialog () {
    this.modalDialogDetails = defaultModalDialogDetails
  }

  confirmAction = (modal: ModalDialogDetails) => {
    this.setDetailsAndShowModalDialog({
      ...modal,
      componentKey: ModalComponentKeyTypes.CONFIRM_DIALOG
    })
  }

  // DIALOG POPUP
  @observable dialogPopupProps: DialogPopupProps = null

  @action openDialogPopup = (props: DialogPopupProps) => {
    this.dialogPopupProps = props
  }

  @action closeDialogPopup = () => {
    this.dialogPopupProps = null
  }
}
